/**
 * API client for modular report profiles.
 * All calls require admin authentication (x-access-token header).
 */

import { joinPath } from "./client";
import { getAdminToken } from "./adminClient";
import { resolveModularProfileId } from "../admin/reportProfileDefaults";
import { openReportLoadingTab } from "../utils/reportPdfLoader";
import { isAbortError } from "../utils/fetchAbort";

function adminHeaders() {
  const token = getAdminToken();
  const headers = { "Content-Type": "application/json" };
  if (token) headers["x-access-token"] = token;
  return headers;
}

async function profileFetch(method, path, body, options = {}) {
  const signal = options.signal;
  try {
    const res = await fetch(joinPath(path), {
      method,
      headers: adminHeaders(),
      body: body != null ? JSON.stringify(body) : undefined,
      signal,
    });
    let json = {};
    try {
      json = await res.json();
    } catch {
      if (isAbortError(null, signal)) {
        const err = new Error("The user aborted a request.");
        err.name = "AbortError";
        throw err;
      }
      if (!res.ok) {
        throw new Error(`Request failed (${res.status})`);
      }
      throw new Error("Invalid response from server");
    }
    if (!res.ok) {
      const err = new Error(json.message || `Request failed (${res.status})`);
      err.code = json.code;
      err.status = res.status;
      throw err;
    }
    return json;
  } catch (err) {
    if (isAbortError(err, signal)) {
      const abortErr = err?.name === "AbortError" ? err : new Error("The user aborted a request.");
      abortErr.name = "AbortError";
      throw abortErr;
    }
    throw err;
  }
}

// ---------------------------------------------------------------------------
// Block catalog
// ---------------------------------------------------------------------------

/** Fetch all available report blocks from the server catalog. */
export function fetchBlockCatalog(options = {}) {
  return profileFetch("GET", "v2/report-profiles/blocks", undefined, options);
}

// ---------------------------------------------------------------------------
// Profile CRUD
// ---------------------------------------------------------------------------

/** List all report profiles. */
export function fetchProfiles(includeSystem = true, options = {}) {
  return profileFetch(
    "GET",
    `v2/report-profiles?include_system=${includeSystem}`,
    undefined,
    options
  );
}

/** Get a single profile by profile_id or _id. */
export function fetchProfile(profileId) {
  return profileFetch("GET", `v2/report-profiles/${profileId}`);
}

/**
 * Create a new report profile.
 * @param {object} data - { name, description, target_role, output_format, blocks: [{block_id, order, enabled}] }
 */
export function createProfile(data) {
  return profileFetch("POST", "v2/report-profiles", data);
}

/**
 * Update an existing profile.
 * @param {string} profileId
 * @param {object} data - Partial fields to update
 */
export function updateProfile(profileId, data) {
  return profileFetch("PUT", `v2/report-profiles/${profileId}`, data);
}

/** Delete a profile (system profiles cannot be deleted). */
export function deleteProfile(profileId) {
  return profileFetch("DELETE", `v2/report-profiles/${profileId}`);
}

// ---------------------------------------------------------------------------
// Engagement assignments
// ---------------------------------------------------------------------------

/** Assign a profile to a company+role. org_id is the company_name. */
export function assignProfileToEngagement(orgId, role, profileId) {
  return profileFetch("POST", "v2/report-profiles/assignments", {
    org_id: orgId,
    role: role ?? null,
    profile_id: profileId,
  });
}

/** Get profile assignments for a company. */
export function fetchAssignments(orgId, role = null) {
  const qs = role ? `?role=${encodeURIComponent(role)}` : "";
  return profileFetch("GET", `v2/report-profiles/assignments/${encodeURIComponent(orgId)}${qs}`);
}

/** List company names available for profile assignment. */
export function fetchAssignmentCompanies() {
  return profileFetch("GET", "v2/report-profiles/assignments/companies");
}

/**
 * Assign a profile to one or more companies.
 * @param {{ profile_id: string, org_ids?: string[], all_companies?: boolean, assign_as?: 'company'|'team_report' }} payload
 */
export function assignProfileBulk(payload) {
  return profileFetch("POST", "v2/report-profiles/assignments/bulk", payload);
}

// ---------------------------------------------------------------------------
// Modular report generation
// ---------------------------------------------------------------------------

/**
 * Generate a modular report (PDF or Excel) using a profile.
 * Returns a Blob for download/display.
 *
 * @param {string} companyName
 * @param {string} profileId
 * @param {{ role?: string, format?: 'pdf'|'excel' }} options
 */
export async function generateModularReport(companyName, profileId, options = {}) {
  const resolvedProfileId = resolveModularProfileId(profileId);
  const params = new URLSearchParams({
    company_name: companyName,
    profile_id: resolvedProfileId,
  });
  if (options.role) params.set("role", options.role);
  if (options.format) params.set("format", options.format);

  const res = await fetch(joinPath(`v2/reports/modular/generate?${params}`), {
    method: "GET",
    headers: adminHeaders(),
  });

  const contentType = res.headers.get("content-type") || "";
  if (!res.ok) {
    if (contentType.includes("application/json")) {
      const json = await res.json().catch(() => ({}));
      throw new Error(json.message || `Report generation failed (${res.status})`);
    }
    throw new Error(`Report generation failed (${res.status})`);
  }
  if (contentType.includes("application/json")) {
    const json = await res.json().catch(() => ({}));
    throw new Error(json.message || "Report generation returned an error");
  }
  return res.blob();
}

function modularReportLoadingMessage(profileId) {
  if (profileId === "company_analyzed") return "Generating analyzed company report…";
  if (profileId === "team_analyzed") return "Generating analyzed team report…";
  return "Generating report…";
}

function downloadPdfBlob(blob, companyName, suffix = "Report") {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${companyName.replace(/\s+/g, "_")}_${suffix}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/**
 * Open a modular PDF report in a new browser tab (popup-safe: tab opens on click).
 * @returns {{ openedInTab: boolean, downloaded: boolean }}
 */
export async function openModularReportPdf(companyName, profileId, role = "") {
  const resolvedProfileId = resolveModularProfileId(profileId);
  const loadingMessage = modularReportLoadingMessage(resolvedProfileId);
  const win = openReportLoadingTab(loadingMessage);

  try {
    const blob = await generateModularReport(companyName, resolvedProfileId, {
      format: "pdf",
      role,
    });
    const url = URL.createObjectURL(blob);
    if (win) {
      win.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      return { openedInTab: true, downloaded: false };
    }

    const suffix =
      resolvedProfileId === "company_analyzed"
        ? "Analyzed_Company_Report"
        : resolvedProfileId === "team_analyzed"
          ? "Analyzed_Team_Report"
          : "Report";
    downloadPdfBlob(blob, companyName, suffix);
    return { openedInTab: false, downloaded: true };
  } catch (err) {
    if (win) win.close();
    throw err;
  }
}

/**
 * Download a modular Excel report.
 */
export async function downloadModularReportExcel(companyName, profileId, role = "") {
  const blob = await generateModularReport(companyName, profileId, {
    format: "excel",
    role,
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${companyName.replace(/\s+/g, "_")}_Report.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 5_000);
}

/**
 * Fetch JSON preview of what a profile would contain for a company
 * (no PDF/Excel generated — just the scored data).
 */
export function previewModularReport(companyName, profileId, role = "") {
  const params = new URLSearchParams({ company_name: companyName, profile_id: profileId });
  if (role) params.set("role", role);
  return profileFetch("GET", `v2/reports/modular/preview?${params}`);
}

// ---------------------------------------------------------------------------
// AI Executive Summary (standalone one-pager per role)
// ---------------------------------------------------------------------------

export const EXEC_SUMMARY_ROLES = [
  { id: "CEO", label: "CEO", description: "Visual pillar snapshot, importance vs agreement chart, delivers vs. breaks" },
  { id: "Leadership Team", label: "Leadership Team", description: "Alignment snapshot, importance vs agreement chart, align vs. diverge" },
  { id: "PE Sponsor", label: "PE Sponsor", description: "Value-creation verdict, pillar chart, value at risk" },
  { id: "Board Member", label: "Board Member", description: "Governance snapshot, pillar chart, oversight concerns" },
];

/** Check if company has completed surveys for executive summary (lightweight). */
export function fetchExecutiveSummaryStatus(companyName, options = {}) {
  const params = new URLSearchParams({ company_name: companyName });
  return profileFetch(
    "GET",
    `v2/reports/modular/executive-summary/status?${params}`,
    undefined,
    options
  );
}

/** Batch lightweight readiness for the company profile table. */
export function fetchExecutiveSummaryStatusBatch(companyNames, options = {}) {
  return profileFetch(
    "POST",
    "v2/reports/modular/executive-summary/status/batch",
    { company_names: companyNames },
    options
  );
}

/** JSON preview of executive summary (no PDF). */
export function previewExecutiveSummary(companyName, role = "CEO") {
  const params = new URLSearchParams({ company_name: companyName, role });
  return profileFetch("GET", `v2/reports/modular/executive-summary/preview?${params}`);
}

/** Generate one-page executive summary PDF. Returns Blob. */
export async function generateExecutiveSummaryPdf(companyName, role = "CEO") {
  const params = new URLSearchParams({ company_name: companyName, role });
  const res = await fetch(joinPath(`v2/reports/modular/executive-summary?${params}`), {
    method: "GET",
    headers: adminHeaders(),
  });
  const contentType = res.headers.get("content-type") || "";
  if (!res.ok) {
    if (contentType.includes("application/json")) {
      const json = await res.json().catch(() => ({}));
      throw new Error(json.message || `Executive summary failed (${res.status})`);
    }
    throw new Error(`Executive summary failed (${res.status})`);
  }
  if (contentType.includes("application/json")) {
    const json = await res.json().catch(() => ({}));
    throw new Error(json.message || "Executive summary returned an error");
  }
  return res.blob();
}

// ---------------------------------------------------------------------------
// Lite company reports (admin)
// ---------------------------------------------------------------------------

export function fetchLiteReportCompanies() {
  return profileFetch("GET", "v2/reports/modular/lite/companies");
}

export async function generateLiteCompanyReport(companyName) {
  const params = new URLSearchParams({ company_name: companyName });
  const res = await fetch(joinPath(`v2/reports/modular/lite/generate?${params}`), {
    method: "GET",
    headers: adminHeaders(),
  });
  const contentType = res.headers.get("content-type") || "";
  if (!res.ok) {
    if (contentType.includes("application/json")) {
      const json = await res.json().catch(() => ({}));
      throw new Error(json.message || `Lite report failed (${res.status})`);
    }
    throw new Error(`Lite report failed (${res.status})`);
  }
  if (contentType.includes("application/json")) {
    const json = await res.json().catch(() => ({}));
    throw new Error(json.message || "Lite report returned an error");
  }
  return res.blob();
}

export async function openLiteCompanyReportPdf(companyName) {
  const win = openReportLoadingTab("Generating lite report…");
  try {
    const blob = await generateLiteCompanyReport(companyName);
    const url = URL.createObjectURL(blob);
    if (win) {
      win.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      return { openedInTab: true, downloaded: false };
    }
    const a = document.createElement("a");
    a.href = url;
    a.download = `${companyName.replace(/\s+/g, "_")}_Lite_Report.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    return { openedInTab: false, downloaded: true };
  } catch (err) {
    if (win) win.close();
    throw err;
  }
}
